/**
 * Divergence-free turbulence: u = curl(e * psi).
 *
 * psi is a vector potential built from random Fourier modes:
 *
 *   psi(x, t) = sum_m A_m sin(k_m . x + phi_m + omega_m t)
 *
 * This is the kinematic-simulation idea: Fung, J. C. H., Hunt, J. C. R.,
 * Malik, N. A., and Perkins, R. J. (1992). Kinematic simulation of
 * homogeneous turbulence by unsteady random Fourier modes. Journal of
 * Fluid Mechanics, 236, 281-318. doi:10.1017/S0022112092001423
 *
 * The envelope e(r, y) shapes where turbulence lives (strong near the
 * eyewall, weak in the eye and far out, fading with height). Putting e
 * inside the curl keeps the field divergence-free, which multiplying the
 * curl by e afterwards would not:
 *
 *   curl(e psi) = e curl(psi) + grad(e) x psi
 *
 * Bridson, R., Hourihan, J., and Nordenstam, M. (2007). Curl-Noise for
 * Procedural Fluid Flow. ACM Transactions on Graphics, 26(3), Article 46.
 * doi:10.1145/1276377.1276435. Bridson et al. modulate the potential the
 * same way to impose boundaries.
 *
 * Deviation from BUILD_SPEC.md section 6.2, which suggested finite
 * differences: the curl is analytic here. For a Fourier mode,
 * curl(A sin(k.x + phi)) = cos(k.x + phi) (k x A), which is exact and
 * costs one sincos per mode instead of six potential evaluations.
 * divergence.test.ts checks the result with finite differences.
 */
import { Rng } from '../core/rng';

export interface CurlNoiseParams {
  seed: number;
  modes: number;
  /** Largest eddy wavelength, m. */
  maxWavelengthM: number;
  /** Smallest eddy wavelength, m. */
  minWavelengthM: number;
  /** RMS velocity scale where the envelope is 1, m/s. */
  amplitudeMps: number;
  /** Envelope peak radius (usually Rmax), m. */
  rPeakM: number;
  /** Envelope e-folding height, m. */
  heightScaleM: number;
  /** Mode decorrelation time, s. */
  periodS: number;
}

/** Read-only view of the mode tables, for the GPU port (gpu/windFieldTsl.ts). */
export interface CurlNoiseModes {
  readonly count: number;
  readonly kx: Float64Array;
  readonly ky: Float64Array;
  readonly kz: Float64Array;
  readonly ax: Float64Array;
  readonly ay: Float64Array;
  readonly az: Float64Array;
  /** k x A per mode. */
  readonly cx: Float64Array;
  readonly cy: Float64Array;
  readonly cz: Float64Array;
  readonly phase: Float64Array;
  readonly omega: Float64Array;
  /** Normalization so RMS |curl psi| = 1. */
  readonly velNorm: number;
}

export interface CurlNoise {
  readonly params: CurlNoiseParams;
  /** Mode tables. velNorm reflects the last refresh(). */
  modes(): CurlNoiseModes;
  /** Recompute envelope constants after mutating amplitude, rPeakM, or heightScaleM. */
  refresh(): void;
  /**
   * Add gain * curl(e psi) at storm-relative position (dx, y, dz) to `out`.
   * Allocation-free.
   */
  addTo(
    dxM: number,
    yM: number,
    dzM: number,
    tS: number,
    gain: number,
    out: { x: number; y: number; z: number },
  ): void;
}

export function createCurlNoise(params: CurlNoiseParams): CurlNoise {
  const n = params.modes;
  // Per-mode wave vector k, amplitude A, phase, angular frequency.
  const kx = new Float64Array(n);
  const ky = new Float64Array(n);
  const kz = new Float64Array(n);
  const ax = new Float64Array(n);
  const ay = new Float64Array(n);
  const az = new Float64Array(n);
  const ph = new Float64Array(n);
  const om = new Float64Array(n);
  // Precomputed k x A per mode.
  const cx = new Float64Array(n);
  const cy = new Float64Array(n);
  const cz = new Float64Array(n);

  const rng = new Rng(params.seed);
  for (let m = 0; m < n; m++) {
    // Log-spaced wavelengths, random direction.
    const frac = n === 1 ? 0 : m / (n - 1);
    const lambda =
      params.maxWavelengthM * Math.pow(params.minWavelengthM / params.maxWavelengthM, frac);
    const kMag = (2 * Math.PI) / lambda;
    let dx = rng.normal();
    let dy = rng.normal();
    let dz = rng.normal();
    const dn = Math.hypot(dx, dy, dz) || 1;
    dx /= dn;
    dy /= dn;
    dz /= dn;
    kx[m] = kMag * dx;
    ky[m] = kMag * dy;
    kz[m] = kMag * dz;
    // Amplitude ~ 1/k so every mode contributes similar velocity (|k x A| ~ 1).
    ax[m] = rng.normal() / kMag;
    ay[m] = rng.normal() / kMag;
    az[m] = rng.normal() / kMag;
    ph[m] = rng.range(0, 2 * Math.PI);
    om[m] = ((2 * Math.PI) / params.periodS) * rng.range(0.5, 1.5);
  }

  let velNorm = 1;
  function refresh(): void {
    // Normalize so RMS |curl psi| over random positions is 1:
    // E[cos^2] = 1/2 per mode, modes uncorrelated.
    let sum = 0;
    for (let m = 0; m < n; m++) {
      const k0 = kx[m] as number;
      const k1 = ky[m] as number;
      const k2 = kz[m] as number;
      const a0 = ax[m] as number;
      const a1 = ay[m] as number;
      const a2 = az[m] as number;
      const c0 = k1 * a2 - k2 * a1;
      const c1 = k2 * a0 - k0 * a2;
      const c2 = k0 * a1 - k1 * a0;
      cx[m] = c0;
      cy[m] = c1;
      cz[m] = c2;
      sum += 0.5 * (c0 * c0 + c1 * c1 + c2 * c2);
    }
    velNorm = sum > 0 ? 1 / Math.sqrt(sum) : 0;
  }
  refresh();

  return {
    params,
    refresh,
    modes(): CurlNoiseModes {
      return {
        count: n,
        kx,
        ky,
        kz,
        ax,
        ay,
        az,
        cx,
        cy,
        cz,
        phase: ph,
        omega: om,
        velNorm,
      };
    },
    addTo(dxM, yM, dzM, tS, gain, out): void {
      if (gain === 0 || params.amplitudeMps === 0) return;
      // Envelope e(r, y) = E_r(r) * E_y(y)
      //   E_r = s^2 exp(1 - s^2), s = r / rPeak   (peaks at 1 when r = rPeak)
      //   E_y = exp(-y / H)                         (y < 0 treated as 0)
      const rp = params.rPeakM;
      const H = params.heightScaleM;
      const r2 = dxM * dxM + dzM * dzM;
      const s2 = r2 / (rp * rp);
      const er = s2 * Math.exp(1 - s2);
      const yy = yM > 0 ? yM : 0;
      const ey = Math.exp(-yy / H);
      const e = er * ey;
      if (e < 1e-6) return;
      // grad e, by the chain rule through s^2:
      // dE_r/d(s^2) = exp(1 - s^2) (1 - s^2),  d(s^2)/dx = 2x / rp^2.
      const dEr = Math.exp(1 - s2) * (1 - s2);
      const gx = dEr * ((2 * dxM) / (rp * rp)) * ey;
      const gz = dEr * ((2 * dzM) / (rp * rp)) * ey;
      const gy = yM > 0 ? (-er * ey) / H : 0;

      let ux = 0;
      let uy = 0;
      let uz = 0;
      let px = 0;
      let py = 0;
      let pz = 0;
      for (let m = 0; m < n; m++) {
        const theta =
          (kx[m] as number) * dxM +
          (ky[m] as number) * yM +
          (kz[m] as number) * dzM +
          (ph[m] as number) +
          (om[m] as number) * tS;
        const c = Math.cos(theta);
        const sn = Math.sin(theta);
        ux += c * (cx[m] as number);
        uy += c * (cy[m] as number);
        uz += c * (cz[m] as number);
        px += sn * (ax[m] as number);
        py += sn * (ay[m] as number);
        pz += sn * (az[m] as number);
      }
      // curl(e psi) = e curl(psi) + grad(e) x psi
      const k = gain * params.amplitudeMps * velNorm;
      out.x += k * (e * ux + (gy * pz - gz * py));
      out.y += k * (e * uy + (gz * px - gx * pz));
      out.z += k * (e * uz + (gx * py - gy * px));
    },
  };
}
